import React from 'react';
import { useTranslate } from '@translations/useTranslate';
import PrivacyNoticeStyles from './PrivacyNotice.module.scss';

const PrivacyNotice = () => {
  const { t } = useTranslate();
  return (
    <>
      <h5 className={PrivacyNoticeStyles.title}>{t('privacy.notice.title')}</h5>
      <div className={PrivacyNoticeStyles.privacyNoticeBox}>
        <div>
          <p className={PrivacyNoticeStyles.styledParagraph}>
            TAWAL has established this Privacy Notice for the handling of Personal Data of Visitors
            (hereinafter referred to as “You”, “Your” or “Yours”). This Privacy Notice is prepared
            in compliance with the Personal Data Protection Law (PDPL). It explains Your rights
            under the PDPL and describes the types of Personal Data TAWAL collects from You and how
            it handles Your Personal Data. By visiting Tawal premises, You acknowledge Your
            agreement to this Privacy Notice.
          </p>

          <ol className={PrivacyNoticeStyles.mainList}>
            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>Definitions</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                All terms in this Privacy Notice shall follow the same definitions as outlined in
                TAWAL’s general Privacy Notice. The following definitions are particularly relevant
                to Visitors:
              </p>
              <ol className={PrivacyNoticeStyles.subList}>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>“Personal Data”</span> means any
                  data, regardless of its source or form, that may lead to identifying an individual
                  specifically, or that may directly or indirectly make it possible to identify an
                  individual, including name, personal identification number, addresses, contact
                  numbers, license numbers, records, personal assets, bank and credit card numbers,
                  photos and videos of an individual, and any other data of personal nature.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>“PDPL”</span> means the Personal
                  Data Protection Law in the Kingdom of Saudi Arabia issued by a Royal Decree M/19
                  dated 09/02/1443AH corresponding to September 16, 2021, and approving resolution
                  No. 98 dated 07/02/1443AH corresponding to September 2021, which the latest update
                  to the PDPL came into force on September 14, 2023. Any reference to PDPL shall
                  include the latest amendment and update thereof and any subsequent amendments or
                  updates thereto.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>“Privacy Notice”</span> means this
                  privacy notice including its terms and conditions stipulated herein.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>“Regulations”</span> the applicable
                  implementing regulations to the PDPL.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>“TAWAL”</span> means
                  Telecommunications Towers Company, a Saudi company incorporated under the
                  Commercial Law of the Kingdom of Saudi Arabia. Any use of the words “We”, “Our” or
                  “Us” means and shall refer to TAWAL.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>“Controller”</span> means any
                  natural or legal person, public or private entity, or any other body that alone or
                  jointly with others determines the purposes and means of processing Personal Data.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>“Processor”</span> means any
                  natural or legal person, public or private entity, or any other body that
                  processes personal data on behalf of the Controller.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>“Visitor”</span> means any
                  individual entering or attempting to access TAWAL&#39;s physical premises,
                  regardless of purpose (e.g., customers, partners, vendors, government officials,
                  or job seekers).
                </li>
              </ol>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>Who are we</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                TAWAL, is the Kingdom of Saudi Arabia’s first licensed Telecommunications Tower
                Company and the largest in the region, providing leading integrated ICT
                infrastructure. As the Controller of your Personal Data, our details are as follows:
              </p>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                <strong>Registered Address:</strong> 3210 Al-Thumama Road, Al-Sahafa District,
                Riyadh 13315 - 6228
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                Legal Basis for Processing Your Personal Data Could Be
              </strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                TAWAL processes your Personal Data as per the principles stated in Personal Data
                Protection Law (PDPL) and its Implementing Regulation. The processing of your
                Personal Data will be on one of the following grounds:
              </p>
              <ul className={PrivacyNoticeStyles.bulletList}>
                <li>
                  <strong className={PrivacyNoticeStyles.boldText}>Explicit Consent:</strong>{' '}
                  Obtained when required, such as before collecting CCTV footage in areas where
                  consent is mandated.
                </li>
                <li>
                  <strong className={PrivacyNoticeStyles.boldText}>Legal Obligation:</strong>{' '}
                  Compliance with safety, public health, and national security laws and regulations.
                </li>
                <li>
                  <strong className={PrivacyNoticeStyles.boldText}>Legitimate Interest:</strong>{' '}
                  TAWAL’s interest in ensuring the safety and operational continuity of its sites.
                </li>
              </ul>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                The Purposes of this Data Collection Include but Not Limited To:
              </strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                The Personal Data We collect from You as a visitor is necessary for the following
                purposes:
              </p>
              <ul className={PrivacyNoticeStyles.bulletList}>
                <li>
                  Ensuring the safety and security of TAWAL’s premises, employees, and visitors
                </li>
                <li>Verifying the identity of individuals entering the premises</li>
                <li>
                  Maintaining records of visitor activity for legal, audit, or investigative
                  purposes
                </li>
                <li>
                  Complying with legal and regulatory obligations, including occupational health and
                  safety requirements
                </li>
                <li>Managing emergency situations (e.g., health incidents, evacuation)</li>
                <li>Investigating incidents or complaints raised during or after a visit</li>
              </ul>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                Tawal will not subsequently process Personal Data in a manner inconsistent with the
                original Collection purpose
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                What Personal Data We Collect and Use
              </strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                Depending on the reason for Your visit, We may collect and process the following
                types of Personal Data:
              </p>
              <ul className={PrivacyNoticeStyles.bulletList}>
                <li>Full name</li>
                <li>National ID or Iqama number</li>
                <li>Company name or affiliation</li>
                <li>Purpose of visit</li>
                <li>Contact number or email (optional)</li>
                <li>Vehicle license plate (if applicable)</li>
                <li>Entry and exit timestamps</li>
                <li>CCTV footage captured on-site</li>
                <li>Sign-in and sign-out data</li>
              </ul>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                How We Collect Your Personal Data
              </strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                We collect Your Personal Data directly from You or via surveillance systems in the
                following ways:
              </p>
              <ul className={PrivacyNoticeStyles.bulletList}>
                <li>Physical registration: At security gates or reception desks</li>
                <li>Security logs and scanners: At entrances or controlled-access areas</li>
                <li>
                  CCTV cameras: Placed throughout common areas and critical infrastructure zones
                </li>
                <li>
                  Incident or complaint documentation: If You are involved in an incident during
                  Your visit
                </li>
              </ul>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                How Long We Store Your Personal Data
              </strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                TAWAL retains Your Personal Data only for as long as is necessary to fulfill the
                purposes stated in this Privacy Notice or as required by applicable laws and
                regulations.
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                Data Protection and Disclosure
              </strong>
              <ol className={PrivacyNoticeStyles.subList}>
                <li>
                  We take Your Personal Data protection seriously and implement appropriate
                  organizational and technical measures such as audits, staff training, and regular
                  reporting to prevent unauthorized disclosure or processing TAWAL maintains the
                  privacy and confidentiality of all Personal Data, disclosing it only when legally
                  required, approved, or necessary to provide products, services, or technical
                  support, in line with the “Why We Collect and Use Your Personal Data” section.
                </li>
                <li>
                  TAWAL does not rent, sell, exchange, or transfer Your Personal Data to third
                  parties outside TAWAL or its partners without Your consent, except for anonymized
                  data used for analysis and reporting in compliance with PDPL and related
                  regulations.
                </li>
                <li>
                  TAWAL may disclose your personal data to other parties providing data processing,
                  background verification services, etc., as well as to judicial and/or applicable
                  regulatory authorities or other parties as required by law or regulatory
                  obligations applicable to TAWAL or you.
                </li>
                <li>
                  TAWAL will not transfer or disclose Personal Data outside Saudi Arabia unless it
                  is required by international obligations or serves the broader interests of the
                  Kingdom.
                </li>
              </ol>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>Your Rights</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                You have the following rights under the PDPL regarding the Personal Data we collect
                about You during Your visit:
              </p>
              <ul className={PrivacyNoticeStyles.bulletList}>
                <li>Right to be informed: About what data is collected, why, and how it is used</li>
                <li>Right to access: Request to view the Personal Data held about You</li>
                <li>Right to obtain a copy: Receive a copy of Your Personal Data upon request</li>
                <li>Right to request rectification: Update or correct inaccurate information</li>
                <li>
                  Right to request erasure: Request deletion of Personal Data no longer needed
                </li>
                <li>
                  Right to object or withdraw consent: Where processing is based on consent, You may
                  revoke it
                </li>
              </ul>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                TAWAL is committed to informing You of Your rights and has established dedicated
                channels to enable You to exercise them effectively. We aim to address all requests
                or complaints within 30 days and maintain records of all interactions.
              </p>

              <p className={PrivacyNoticeStyles.styledParagraph}>
                If You wish to exercise Your rights, or have privacy-related inquiries, please
                contact Us using the details in the “Contact Us With Your Questions and Comments”
                section.
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                Changes to This Privacy Notice
              </strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                To ensure that We continue to offer the best possible experience and the highest
                level of protection to You, We reserve the right to update this Privacy Notice as
                and when necessary and appropriate, including but not limited to compliance with the
                requirements of the PDPL and the Regulations. Any update to this Privacy Notice will
                be available here. We therefore recommend You check it periodically.
              </p>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                This version was last updated on: 10-Sep-2025
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                Governing Law and Jurisdiction
              </strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                This Privacy Notice shall be governed by and construed in accordance with the
                applicable laws of the Kingdom of Saudi Arabia. Any dispute arising out or relating
                to this Privacy Notice shall be subject to the exclusive jurisdiction of the courts
                of the Kingdom of Saudi Arabia.
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                Contact Us With Your Questions and Comments
              </strong>
              <div className={PrivacyNoticeStyles.contactInfo}>
                <p className={PrivacyNoticeStyles.styledParagraph}>
                  If You have any questions or wish to exercise any of Your rights under this
                  Privacy Notice, please contact:
                </p>
                <p>
                  <strong>Data Governance Office</strong>
                </p>
                <p>Email: dgo@tawal.com.sa</p>
                <p>Phone: 8001240008</p>
              </div>
            </li>
          </ol>
        </div>

        <div dir="rtl">
          <p className={PrivacyNoticeStyles.styledParagraph}>
            أعدّت توال هذا الإشعار لحماية خصوصية البيانات الشخصية للزوّار (ويُشار إليكم فيما بعد بـ
            &quot;أنت&quot;، &quot;لك&quot;، أو &quot;لكم&quot;). تم إعداد إشعار الخصوصية امتثالاً
            لأحكام نظام حماية البيانات الشخصية (PDPL). يوضح هذا الإشعار حقوقك بموجب النظام، ويصف
            أنواع البيانات الشخصية التي تجمعها توال منك، وكيفية معالجتها. من خلال زيارتك لمقرات
            توال، فإنك تُقر بموافقتك على هذا الإشعار
          </p>

          <ol className={PrivacyNoticeStyles.mainList}>
            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>التعريفات</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                جميع المصطلحات الواردة في هذا الإشعار تتبع التعريفات المذكورة في الإشعار العام
                لخصوصية توال. وفيما يلي بعض المصطلحات ذات الصلة بتوظيف المرشحين:
              </p>
              <ol className={PrivacyNoticeStyles.subList}>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>
                    &quot;البيانات الشخصية&quot; :
                  </span>{' '}
                  تعني أي بيانات، مهما كان مصدرها أو شكلها، تؤدي إلى التعرف على شخص معين بشكل مباشر
                  أو غير مباشر، مثل الاسم، رقم الهوية، العناوين، أرقام الاتصال، أرقام الرخص،
                  السجلات، الممتلكات الشخصية، أرقام الحسابات البنكية وبطاقات الائتمان، الصور،
                  الفيديوهات، وأي بيانات ذات طابع شخصي.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>&quot;PDPL&quot; :</span> نظام
                  حماية البيانات الشخصية في المملكة العربية السعودية، الصادر بموجب المرسوم الملكي
                  رقم (م/19) بتاريخ 09/02/1443هـ الموافق 16 سبتمبر 2021م، والقرار رقم (98) بتاريخ
                  07/02/1443هـ. وقد دخل التحديث الأخير حيز التنفيذ في 14 سبتمبر 2023م.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>&quot;إشعار الخصوصية&quot; :</span>{' '}
                  هذا الإشعار بكل ما يتضمنه من شروط وأحكام.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>&quot;اللوائح&quot; :</span>{' '}
                  اللائحة التنفيذية المطبقة لنظام حماية البيانات الشخصية.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>&quot;توال&quot; :</span> شركة
                  أبراج الاتصالات، وهي شركة سعودية مسجلة بموجب النظام التجاري في المملكة. أي إشارة
                  إلى &quot;نحن&quot;، &quot;لنا&quot; أو &quot;خاصتنا&quot; تعني توال.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>&quot;المتحكم&quot; :</span> يعني
                  أي شخص طبيعي أو اعتباري، جهة عامة أو خاصة، يحدد بمفرده أو مع غيره غرض ووسيلة
                  معالجة البيانات الشخصية.
                </li>
                <li>
                  <span className={PrivacyNoticeStyles.boldText}>&quot;المعالج&quot; :</span>أي شخص
                  طبيعي أو اعتباري، جهة عامة أو خاصة، يقوم بمعالجة البيانات نيابة عن المتحكم.
                </li>
              </ol>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>من نحن</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                توال هي أول شركة مرخصة لتشغيل أبراج الاتصالات في المملكة، والأكبر في المنطقة، وتوفر
                بنية تحتية متكاملة لتكنولوجيا المعلومات والاتصالات. وكونها متحكمًا في بياناتك
                الشخصية، فإن بياناتنا هي:
              </p>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                <strong>العنوان المسجل:</strong> طريق الثمامة 3210، حي الصحافة، الرياض 6213315-{' '}
              </p>
            </li>

            <li>
              <strong>الأساس القانوني لمعالجة بياناتك الشخصية</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                تقوم توال بمعالجة بياناتك الشخصية وفقًا للمبادئ المحددة في نظام حماية البيانات
                الشخصية ولائحته التنفيذية، وذلك استنادًا إلى أحد الأسس التالية:
              </p>
              <ul className={PrivacyNoticeStyles.bulletList}>
                <li>
                  <strong className={PrivacyNoticeStyles.boldText}>الموافقة الصريحة:</strong> تُنال
                  عند الاقتضاء، مثلًا قبل جمع لقطات كاميرات المراقبة في الأماكن التي يُشترط فيها
                  الحصول على الموافقة{' '}
                </li>
                <li>
                  <strong className={PrivacyNoticeStyles.boldText}>الالتزام القانوني:</strong>{' '}
                  الامتثال لقوانين وأنظمة السلامة والصحة العامة والأمن الوطني.
                </li>
                <li>
                  <strong className={PrivacyNoticeStyles.boldText}>المصلحة المشروعة:</strong>مصلحة
                  شركة توال في ضمان سلامة مواقعها واستمرارية تشغيلها.{' '}
                </li>
              </ul>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>أغراض جمع البيانات</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                تُستخدم بياناتك الشخصية في توال لأغراض تشمل (دون حصر):
              </p>
              <ul className={PrivacyNoticeStyles.bulletList}>
                <li>ضمان سلامة وأمن مرافق توال وموظفيها وزوارها</li>
                <li>التحقق من هوية الأفراد الداخلين إلى المرافق</li>
                <li>الاحتفاظ بسجلات نشاط الزوار لأغراض قانونية أو تدقيقية أو تحقيقية</li>
                <li>
                  الامتثال للالتزامات القانونية والتنظيمية، بما في ذلك متطلبات الصحة والسلامة
                  المهنية
                </li>
                <li>إدارة حالات الطوارئ (مثل الحوادث الصحية أو الإخلاء)</li>
                <li>التحقيق في الحوادث أو الشكاوى التي تُثار أثناء الزيارة أو بعدها</li>
              </ul>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                لن تقوم توال بعد ذلك بمعالجة البيانات الشخصية بطريقة تتعارض مع غرض الجمع الأصلي
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                البيانات الشخصية التي نجمعها
              </strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                تعتمد المعلومات التي تجمعها توال على الحالة، وتشمل عادة :
              </p>
              <ul className={PrivacyNoticeStyles.bulletList}>
                <li>الاسم الكامل</li>
                <li>رقم الهوية الوطنية أو رقم الإقامة</li>
                <li>اسم الشركة أو الجهة المرتبطة بك</li>
                <li>غرض الزيارة</li>
                <li>رقم الاتصال أو البريد الإلكتروني (اختياري)</li>
                <li>رقم لوحة المركبة (إن وجد)</li>
                <li>وقت الدخول والخروج</li>
                <li>(CCTV) تسجيلات كاميرات المراقبة</li>
                <li>بيانات التسجيل عند الدخول والخروج</li>
              </ul>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>طرق جمع بياناتك</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                سوف نقوم بجمع هذه المعلومات منك مباشرة، وفي بعض الظروف، من أطراف ثالثة، مثل الحكام،
                أو وكالات التوظيف، أو موظفي توال، أو الأشخاص الآخرين المرتبطين.
              </p>
              <ul className={PrivacyNoticeStyles.bulletList}>
                <li>التسجيل الفعلي عند بوابات الأمن أو مكاتب الاستقبال</li>
                <li>سجلات الأمان وأجهزة الفحص عند المداخل والمناطق المحددة</li>
                <li>كاميرات المراقبة المنتشرة في المناطق العامة ومناطق البنية التحتية الحساسة</li>
                <li>توثيق الحوادث أو الشكاوى في حال وقوعها أثناء زيارتك</li>
              </ul>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>مدة الاحتفاظ ببياناتك</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                تحتفظ توال ببياناتك الشخصية فقط بقدر ما هو ضروري لتحقيق الأغراض المذكورة في إشعار
                الخصوصية هذا أو حسبما تقتضيه القوانين واللوائح المعمول بها.
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                حماية البيانات والإفصاح عنها
              </strong>
              <ol className={PrivacyNoticeStyles.subList}>
                <li>
                  نحرص على حماية بياناتك الشخصية من خلال اتخاذ التدابير التنظيمية والفنية المناسبة،
                  مثل عمليات التدقيق، وتدريب الموظفين، والتقارير الدورية، لمنع الإفصاح أو المعالجة
                  غير المصرح بها. تحافظ توال على خصوصية وسرية جميع بياناتك الشخصية، ولا تكشف عنها
                  إلا عند الاقتضاء أو الموافقة عليها قانونًا، أو عند الضرورة لتوفير المنتجات أو
                  الخدمات أو الدعم الفني، بما يتماشى مع قسم &quot;لماذا نجمع بياناتك الشخصية
                  ونستخدمها&quot;.
                </li>
                <li>
                  لا تؤجر توال أو تبيع أو تتبادل بياناتك الشخصية مع جهات خارجية خارج توال أو شركائها
                  دون موافقتك، باستثناء البيانات مجهولة المصدر المستخدمة للتحليل وإعداد التقارير
                  وفقًا لقانون حماية البيانات الشخصية واللوائح ذات الصلة. قد نشارك بياناتك مع جهات
                  خارجية موثوقة تدعم أعمالنا أو خدماتنا، شريطة أن تحافظ على السرية.
                </li>
                <li>
                  قد تكشف توال عن بياناتك الشخصية لأطراف خارجية للأغراض المذكورة، بما في ذلك الجهات
                  التي تقدم خدمات معالجة البيانات، أو الخدمات المهنية أو الإدارية، بالإضافة إلى
                  خدمات التأمين أو الصحة أو الخدمات القانونية.
                </li>
                <li>
                  يجوز لشركة توال الإفصاح عن بياناتك الشخصية لأطراف أخرى تقدم خدمات معالجة البيانات
                  والتحقق من الخلفية، وما إلى ذلك، وكذلك للسلطات القضائية و/أو التنظيمية المختصة أو
                  لأطراف أخرى وفقًا لما يقتضيه القانون أو الالتزامات التنظيمية المطبقة على توال أو
                  عليك.
                </li>
                <li>
                  لن تنقل توال أو تكشف عن بياناتك الشخصية خارج المملكة العربية السعودية إلا إذا كان
                  ذلك مطلوبًا بموجب التزامات دولية أو يخدم المصالح الأوسع للمملكة.
                </li>
              </ol>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>حقوقك</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                بصفتك صاحب بيانات شخصية، لك الحقوق التالية وفق نظام حماية البيانات الشخصية واللوائح،
                ما لم تكن هناك قرارات قانونية أو تنظيمية تمنع أو تقيد هذه الحقوق:
              </p>
              <ul className={PrivacyNoticeStyles.bulletList}>
                <li>
                  الحق في المعرفة: بمعرفة أسباب الجمع وطرق المعالجة وبيانات التواصل معنا، ومعرفة إن
                  كان سيتم مشاركة البيانات مع أطراف ثالثة.
                </li>
                <li> حق الوصول إلى بياناتك الشخصية: يمكنك الاطلاع على بياناتك لدى توال.</li>
                <li>
                  حق طلب الحصول على بياناتك الشخصية بصيغة مقروءة: الحصول على نسخة من بياناتك
                  الشخصية.
                </li>
                <li>
                  حق طلب تصحيح بياناتك الشخصية: تصحيح أو تحديث بياناتك غير الدقيقة أو غير الكاملة.
                </li>
                <li>
                  حق طلب إتلاف بياناتك الشخصية: طلب حذف بياناتك لأسباب مثل سحب الموافقة أو انتهاء
                  الغرض من الجمع، مع العلم بأن توال قد تحتفظ بها وفق النظام.
                </li>
                <li>
                  حق الرجوع عن موافقتك على معالجة بياناتك الشخصية: سحب موافقتك أو منع الرسائل
                  الترويجية.
                </li>
              </ul>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                تُعتبر بياناتك شرطًا مسبقًا قبل التعاقد. وتلتزم توال بإعلامك بحقوقك، وتوفر قنوات
                مخصصة لتمكينك من ممارستها. ونسعى لمعالجة الطلبات خلال 30 يومًا، مع الحفاظ على سجلات
                التفاعلات.
              </p>

              <p className={PrivacyNoticeStyles.styledParagraph}>
                إذا رغبت في ممارسة حقوقك أو لديك استفسارات، يُرجى التواصل معنا عبر بيانات &quot;اتصل
                بنا&quot; أدناه.
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                التغييرات على إشعار الخصوصية هذا
              </strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                لضمان استمرار توال في تقديم أفضل تجربة ممكنة وأعلى مستوى من الحماية لك، نحتفظ بالحق
                في تحديث إشعار الخصوصية هذا كلما دعت الضرورة ووفقاً لما نراه مناسباً، بما في ذلك،
                على سبيل المثال لا الحصر، الامتثال لمتطلبات نظام حماية البيانات الشخصية (PDPL)
                ولائحته التنفيذية.سيكون أي تحديث على إشعار الخصوصية هذا متاحًا هنا. لذا نوصيك
                بالتحقق منه دوريًا.
              </p>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                تم تحديث هذا الإصدار آخر مرة في: 2025-Sep-10
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>
                القانون والاختصاص القضائي
              </strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                يخضع هذا الإشعار لأنظمة المملكة العربية السعودية. وأي نزاع يتعلق به يخضع للاختصاص
                الحصري لمحاكم المملكة العربية السعودية.
              </p>
            </li>

            <li>
              <strong className={PrivacyNoticeStyles.sectionTitle}>اتصل بنا</strong>
              <p className={PrivacyNoticeStyles.styledParagraph}>
                إذا كان لديك أي استفسار أو رغبت في ممارسة أي من حقوقك المتعلقة بهذا الإشعار أو
                بياناتك الشخصية، يمكنك التواصل معنا عبر:
              </p>
              <div className={PrivacyNoticeStyles.contactInfo}>
                <p>
                  <strong>مكتب حوكمة البيانات</strong>
                </p>
                <p>البريد الإلكتروني: dgo@tawal.com.sa</p>
                <p>رقم الهاتف: 8001240008</p>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </>
  );
};

export default PrivacyNotice;
